import React from 'react';
import { observer } from 'mobx-react';
import { useMutation } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';
import { ulid } from 'ulid';

import Button from 'core/components/button/button';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { useToast } from 'core/components/toast';
import { GENERATE_LOE, RESEND_WELCOME_EMAIL, SEND_ACCOUNT_LOGIN_LINK } from './account-access-actions.queries';
import useStyles from './account-access-actions.styles';

// Prefer the friendly server-side message (e.g. the rate-limit explanation surfaced via
// util.error) over Apollo's generic wrapper text.
const extractErrorMessage = (error) =>
  error?.graphQLErrors?.[0]?.message || error?.message || 'Something went wrong. Please try again.';

/**
 * Staff actions at the bottom of the Account Details card (#34481): resend the member's
 * welcome email, or send a one-time account login link by email/SMS. Visible to anyone who
 * can service a policy (`canEditPolicies`). Each action is rate limited per member on the
 * backend; a block comes back as an error toast explaining when they can retry.
 */
const AccountAccessActions = () => {
  const { account } = useStore();
  const { canEditPolicies, canGenerateLoe, isAgency } = useSession();
  const toast = useToast();
  const classes = useStyles();

  const [resendWelcomeEmail, { loading: welcomeLoading }] = useMutation(RESEND_WELCOME_EMAIL);
  const [sendEmailLoginLink, { loading: emailLinkLoading }] = useMutation(SEND_ACCOUNT_LOGIN_LINK);
  const [sendSmsLoginLink, { loading: smsLinkLoading }] = useMutation(SEND_ACCOUNT_LOGIN_LINK);
  const [generateLoe, { loading: loeLoading }] = useMutation(GENERATE_LOE);

  // The LOE is per-policy — only offered when actually on a policy screen. Key off the ROUTE
  // (not the store's last-viewed policy, which is stale on the account landing page — #35417).
  const policyRouteMatch = useRouteMatch('/customer/:id/policy/:policyId');
  const currentPolicyId = policyRouteMatch?.params?.policyId;

  // Disable every action while any one is in flight so a member can't be double-messaged
  // by a quick second click on another button before the first request finishes.
  const anySubmitting = welcomeLoading || emailLinkLoading || smsLinkLoading || loeLoading;

  const notifySuccess = (message) => toast.notify({ type: 'success', message });
  const notifyError = (error) => toast.notify({ type: 'error', message: extractErrorMessage(error) });

  const handleResendWelcome = async () => {
    try {
      await resendWelcomeEmail({ variables: { accountId: account.id } });
      notifySuccess('Welcome email sent to the member.');
    } catch (error) {
      notifyError(error);
    }
  };

  const handleSendLoginLink = (channel, send) => async () => {
    try {
      await send({
        variables: {
          accountId: account.id,
          channel
        }
      });
      notifySuccess(channel === 'SMS' ? 'Login link texted to the member.' : 'Login link emailed to the member.');
    } catch (error) {
      notifyError(error);
    }
  };

  const handleGenerateLoe = async () => {
    try {
      const { data } = await generateLoe({
        // taskRunId is a fresh per-run id (the tool records run history against it).
        variables: { args: { policyId: currentPolicyId }, taskRunId: ulid() }
      });
      const result = data?.generateLoe;
      if (result?.success) {
        notifySuccess('Letter of Experience generated and added to Internal Documents.');
        // Surface the new document without a manual refresh.
        account.policies?.getDocuments?.(account.id, isAgency);
      } else {
        notifyError(new Error(result?.error || 'Could not generate the Letter of Experience.'));
      }
    } catch (error) {
      notifyError(error);
    }
  };

  if (!canEditPolicies) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Button
        mode="big"
        variant="contained"
        color="secondary"
        onClick={handleResendWelcome}
        loading={welcomeLoading}
        disabled={anySubmitting || !account.email}
      >
        Resend Welcome Email
      </Button>
      <Button
        mode="big"
        variant="contained"
        color="secondary"
        onClick={handleSendLoginLink('EMAIL', sendEmailLoginLink)}
        loading={emailLinkLoading}
        disabled={anySubmitting || !account.email}
      >
        Email Login Link
      </Button>
      <Button
        mode="big"
        variant="contained"
        color="secondary"
        onClick={handleSendLoginLink('SMS', sendSmsLoginLink)}
        loading={smsLinkLoading}
        disabled={anySubmitting || !account.phoneNumber}
      >
        SMS Login Link
      </Button>
      {canGenerateLoe && currentPolicyId && (
        <Button
          mode="big"
          variant="contained"
          color="secondary"
          onClick={handleGenerateLoe}
          loading={loeLoading}
          disabled={anySubmitting}
        >
          Generate Letter of Experience
        </Button>
      )}
    </div>
  );
};

export default observer(AccountAccessActions);
